import React, { useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr } from "../lib/api";
import { QuickRange } from "../components/QuickRange";
import { CustomReport } from "../components/CustomReport";
import { BranchSummary } from "../components/BranchSummary";
import { BranchFilter } from "../components/BranchTools";
import { useAuth } from "../context/AuthContext";
import { getRoles } from "../lib/api";
import { FileDown, Sheet, TrendingUp, TrendingDown, HandCoins, ArrowLeftRight, Layers, Ban, FileText, Percent, FileX, Scale } from "lucide-react";
import { toast } from "sonner";

const field = "h-10 px-3 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40";

function monthStart() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function monthEnd() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); }

const TABS = [
  { k: "cashbook", label: "Cash Book", icon: Layers },
  { k: "invoices", label: "Invoices", icon: FileText },
  { k: "credit", label: "Credit Parties", icon: HandCoins },
  { k: "flow", label: "Payment Flow", icon: ArrowLeftRight },
  { k: "expenses", label: "Expenses", icon: TrendingDown },
  { k: "discounts", label: "Discounts", icon: Percent },
  { k: "cancelled", label: "Cancelled Invoices", icon: Ban },
  { k: "closing", label: "Closing Differences", icon: Scale },
  { k: "missing", label: "Missing Invoices", icon: FileX },
  { k: "missingch", label: "Missing Challans", icon: FileX },
  { k: "custom", label: "Custom Report", icon: Sheet, staff: true },
  { k: "branches", label: "Branches", icon: Sheet, staff: true },
];

const signed = (d) => (Math.abs(d) < 0.01 ? "—" : d < 0 ? `−${inr(-d)}` : `+${inr(d)}`);
const tone = (d) => (Math.abs(d) < 0.01 ? "text-muted-foreground" : d < 0 ? "text-destructive" : "text-amber-600");

function ClosingDiffReport({ data, start, end }) {
  const exportCsv = () => {
    if (!data.items.length) return toast.error("Nothing to export");
    const head = ["Date", "Cash (books)", "Cash (counted)", "Cash diff", "Bank (books)", "Bank (actual)", "Bank diff", "Reason", "Counted by"];
    const rows = data.items.map((r) => [r.date, r.cash_book, r.cash_counted, r.cash_diff, r.bank_book, r.bank_counted, r.bank_diff, r.reason, r.by]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `closing_differences_${start}_${end}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };
  const Card = ({ t, v, cls, id }) => (
    <div className={`rounded-lg border px-4 py-3 ${cls}`}><div className="text-xs text-muted-foreground">{t}</div><div data-testid={id} className="num text-xl font-bold">{v}</div></div>
  );
  return (
    <div className="animate-fade-up space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card t="Days counted / with difference" v={`${data.days_counted} / ${data.days_with_difference}`} cls="border-border bg-card" id="closing-days" />
        <Card t="Total shortage" v={inr(data.total_short)} cls="border-destructive/20 bg-destructive/5 text-destructive" id="closing-total-short" />
        <Card t="Cash short / extra" v={`${inr(data.cash_short)} / ${inr(data.cash_extra)}`} cls="border-border bg-card" id="closing-cash" />
        <Card t="Bank short / extra" v={`${inr(data.bank_short)} / ${inr(data.bank_extra)}`} cls="border-border bg-card" id="closing-bank" />
      </div>
      <div className="flex justify-end">
        <button data-testid="closing-export" onClick={exportCsv} className="h-9 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><Sheet size={14} /> Export CSV</button>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-[100px_130px_130px_1fr] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span>Date</span><span className="text-right">Cash diff</span><span className="text-right">Bank diff</span><span>Reason</span>
          </div>
          <div className="divide-y divide-border max-h-[480px] overflow-auto">
            {data.items.map((r) => (
              <div key={r.date} data-testid={`closing-row-${r.date}`} className="grid grid-cols-[100px_130px_130px_1fr] gap-3 px-5 py-3 items-center text-sm">
                <span className="num text-xs font-bold text-primary">{r.date}</span>
                <span className={`num text-right font-semibold ${tone(r.cash_diff)}`} title={`Books ${inr(r.cash_book)} · Counted ${inr(r.cash_counted)}`}>{signed(r.cash_diff)}</span>
                <span className={`num text-right font-semibold ${tone(r.bank_diff)}`} title={`Books ${inr(r.bank_book)} · Actual ${inr(r.bank_counted)}`}>{signed(r.bank_diff)}</span>
                <span className="text-xs">{r.reason || "—"}<span className="text-muted-foreground"> · {r.by}</span></span>
              </div>
            ))}
            {data.items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">{data.days_counted ? "Every counted day matched the books in this range." : "No closing counts in this range yet."}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function fyOptions() {
  const d = new Date();
  const startYear = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  const out = [];
  for (let i = 0; i < 6; i++) {
    const y = startYear - i;
    out.push(`${String(y).slice(2)}-${String(y + 1).slice(2)}`);
  }
  return out;
}

export default function Reports() {
  const [tab, setTab] = useState("cashbook");
  const [branchSum, setBranchSum] = useState(null);
  const [branch, setBranch] = useState("");
  const { user } = useAuth();
  const isStaff = user?.platform_role === "superadmin" || getRoles(user).some((r) => r === "admin" || r === "accountant");
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(monthEnd());
  const [cashbook, setCashbook] = useState(null);
  const [invoicesRep, setInvoicesRep] = useState(null);
  const [invSource, setInvSource] = useState("");
  const [credit, setCredit] = useState(null);
  const [flow, setFlow] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [discounts, setDiscounts] = useState(null);
  const [cancelled, setCancelled] = useState(null);
  const [closing, setClosing] = useState(null);
  const [missing, setMissing] = useState(null);
  const [missMode, setMissMode] = useState("fy");
  const [missFy, setMissFy] = useState(fyOptions()[0]);
  const [missSource, setMissSource] = useState("tata_motors");
  const [missField, setMissField] = useState("invoice");
  const [loading, setLoading] = useState(false);
  const [dailyDate, setDailyDate] = useState(() => new Date().toISOString().slice(0, 10));

  const downloadDaily = async () => {
    try {
      const res = await api.get("/reports/daily", { params: { date: dailyDate }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `daily_report_${dailyDate}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Daily report downloaded");
    } catch { toast.error("Could not generate daily report"); }
  };

  const generate = async (t = tab, s = start, e = end) => {
    setLoading(true);
    try {
      if (t === "cashbook") setCashbook((await api.get("/reports/cashbook", { params: { start: s, end: e, branch } })).data);
      else if (t === "invoices") setInvoicesRep((await api.get("/reports/invoices", { params: { start: s, end: e, source: invSource || undefined } })).data);
      else if (t === "credit") setCredit((await api.get("/reports/credit-parties")).data);
      else if (t === "flow") setFlow((await api.get("/reports/payment-flow", { params: { start: s, end: e } })).data);
      else if (t === "expenses") setExpenses((await api.get("/reports/expenses", { params: { start: s, end: e } })).data);
      else if (t === "discounts") setDiscounts((await api.get("/reports/discounts", { params: { start: s, end: e } })).data);
      else if (t === "cancelled") setCancelled((await api.get("/reports/cancelled-invoices", { params: { start: s, end: e } })).data);
      else if (t === "branches") setBranchSum((await api.get("/reports/branch-summary", { params: { start: s, end: e } })).data);
      else if (t === "closing") setClosing((await api.get("/reports/closing-differences", { params: { start: s, end: e } })).data);
      else if (t === "missing" || t === "missingch") {
        const fld = t === "missingch" ? "challan" : "invoice";
        const params = missMode === "fy" ? { mode: "fy", fy: missFy, source: missSource, field: fld } : { mode: "range", start: s, end: e, source: missSource, field: fld };
        setMissing((await api.get("/reports/missing-invoices", { params })).data);
      }
    } catch { toast.error("Could not load report"); }
    setLoading(false);
  };

  const pick = (s, e) => { setStart(s); setEnd(e); generate(tab, s, e); };
  const switchTab = (t) => { setTab(t); if (t !== "custom") generate(t, start, end); };

  const exportFile = async (format) => {
    try {
      const res = await api.get("/reports/cashbook/export", { params: { start, end, format, branch }, responseType: "blob" });
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      const mime = format === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = `cashbook_${start}_${end}.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${ext.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const label = (t) => t.invoice_no || t.voucher_no || t.receipt_no || "";
  const needsRange = tab !== "credit" && tab !== "missing" && tab !== "custom";

  const exportMissing = () => {
    if (!missing || !missing.groups.length) return toast.error("Nothing to export");
    const head = ["Series", "Missing Serial", "Missing Invoice No"];
    const rows = [];
    missing.groups.forEach((g) => g.missing.forEach((m) => rows.push([g.stem || "(none)", m.serial, m.invoice_no])));
    if (!rows.length) return toast.error("No missing serials to export");
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `missing_serials_${missMode === "fy" ? missFy : `${start}_${end}`}.csv`; a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Missing serials exported");
  };

  const exportInvoices = () => {
    if (!invoicesRep || !invoicesRep.items.length) return toast.error("Nothing to export");
    const head = ["Invoice No", "Date", "Account", "Party (old)", "Driver mobile", "Vehicle", "Type", "Source", "GST", "Amount", "Paid", "Balance", "Status", "Paid Cash", "Paid Online"];
    const rows = invoicesRep.items.map((i) => [i.invoice_no, i.date, i.account_name || i.party_name, i.account_name && i.party_name !== i.account_name ? i.party_name : "", i.driver_mobile || "", i.vehicle_number, i.invoice_type, i.source === "tata_motors" ? "Tata Motors" : "Outside", i.gst ? "Y" : "N", i.amount, i.paid_amount, i.balance, i.status, i.paid_cash, i.paid_online]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `invoices_${start}_${end}.csv`; a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Invoices exported");
  };

  return (
    <Layout>
      <div className="p-8 max-w-6xl mx-auto">
        <PageHeader title="Reports" subtitle="Cash book, credit parties, payment flow and expense analysis.">
          <div className="flex items-end gap-2 rounded-lg border border-accent/20 bg-accent/5 px-3 py-2">
            <div>
              <div className="text-[11px] text-muted-foreground mb-0.5">Daily report date</div>
              <input data-testid="daily-report-date" type="date" className={field} value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} />
            </div>
            <button data-testid="daily-report-download" onClick={downloadDaily}
              className="h-10 px-4 rounded-md bg-accent text-accent-foreground text-sm font-medium flex items-center gap-2 hover:bg-accent/90 transition-colors">
              <FileDown size={15} /> Daily Report (PDF)
            </button>
          </div>
        </PageHeader>

        <div className="inline-flex rounded-lg border border-border bg-card p-1 mb-5 flex-wrap gap-1">
          {TABS.filter((t) => !t.staff || isStaff).map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.k} data-testid={`report-tab-${t.k}`} onClick={() => switchTab(t.k)}
                className={`px-4 h-9 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {needsRange && (
          <div className="rounded-xl border border-border bg-card p-4 mb-6 flex flex-wrap items-end gap-3 animate-fade-up">
            <div className="w-full"><QuickRange onPick={pick} active={[start, end]} /></div>
            <div><div className="text-xs text-muted-foreground mb-1">From</div>
              <input data-testid="report-start" type="date" className={field} value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div><div className="text-xs text-muted-foreground mb-1">To</div>
              <input data-testid="report-end" type="date" className={field} value={end} onChange={(e) => setEnd(e.target.value)} /></div>
            {tab === "cashbook" && <BranchFilter value={branch} onChange={setBranch} testid="cashbook-branch" />}
            <button data-testid="report-generate" onClick={() => generate()} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
              {loading ? "Loading…" : "Generate"}
            </button>
            {tab === "cashbook" && cashbook && (
              <div className="flex gap-2 ml-auto">
                <button data-testid="export-pdf" onClick={() => exportFile("pdf")} className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><FileDown size={15} /> PDF</button>
                <button data-testid="export-xlsx" onClick={() => exportFile("xlsx")} className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><Sheet size={15} /> Excel</button>
              </div>
            )}
          </div>
        )}

        {(tab === "missing" || tab === "missingch") && (
          <div className="rounded-xl border border-border bg-card p-4 mb-6 flex flex-wrap items-end gap-3 animate-fade-up">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Detect gaps by</div>
              <div className="inline-flex rounded-md border border-input bg-card p-1 gap-1">
                {[{ v: "fy", l: "Financial Year" }, { v: "range", l: "Date range" }].map((m) => (
                  <button key={m.v} data-testid={`missing-mode-${m.v}`} onClick={() => setMissMode(m.v)}
                    className={`px-3 h-8 rounded text-xs font-medium transition-colors ${missMode === m.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{m.l}</button>
                ))}
              </div>
            </div>
            {missMode === "fy" ? (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Financial Year</div>
                <select data-testid="missing-fy" className={field} value={missFy} onChange={(e) => setMissFy(e.target.value)}>
                  {fyOptions().map((f) => <option key={f} value={f}>FY {f}</option>)}
                </select>
              </div>
            ) : (
              <>
                <div><div className="text-xs text-muted-foreground mb-1">From</div>
                  <input data-testid="missing-start" type="date" className={field} value={start} onChange={(e) => setStart(e.target.value)} /></div>
                <div><div className="text-xs text-muted-foreground mb-1">To</div>
                  <input data-testid="missing-end" type="date" className={field} value={end} onChange={(e) => setEnd(e.target.value)} /></div>
              </>
            )}
            <div>
              <div className="text-xs text-muted-foreground mb-1">Source</div>
              <select data-testid="missing-source" className={field} value={missSource} onChange={(e) => setMissSource(e.target.value)}>
                <option value="tata_motors">Tata Motors</option>
                <option value="outside">Outside</option>
                <option value="all">All sources</option>
              </select>
            </div>
            <button data-testid="missing-generate" onClick={() => generate(tab)} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
              {loading ? "Loading…" : "Generate"}
            </button>
            {missing && missing.total_missing > 0 && (
              <button data-testid="missing-export" onClick={exportMissing} className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors ml-auto"><FileDown size={15} /> CSV</button>
            )}
          </div>
        )}

        {tab === "cashbook" && cashbook && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 animate-fade-up">
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Opening Cash</div><div className="num text-lg font-semibold">{inr(cashbook.opening_cash)}</div></div>
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Opening Bank</div><div className="num text-lg font-semibold">{inr(cashbook.opening_bank)}</div></div>
              <div className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-3"><div className="text-xs text-muted-foreground">Closing Cash</div><div className="num text-lg font-bold text-accent">{inr(cashbook.closing_cash)}</div></div>
              <div className="rounded-lg border border-primary/15 bg-primary/5 px-4 py-3"><div className="text-xs text-muted-foreground">Closing Bank</div><div className="num text-lg font-bold text-primary">{inr(cashbook.closing_bank)}</div></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-4 py-3 bg-[hsl(var(--positive))]/10 border-b border-border flex items-center gap-2 font-display text-lg font-semibold text-[hsl(var(--positive))]"><TrendingUp size={16} /> Receipts ({cashbook.receipts.length})</div>
                <div className="divide-y divide-border/70 max-h-[420px] overflow-auto">
                  {cashbook.receipts.map((t) => (
                    <div key={t.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <div className="min-w-0"><div className="truncate font-medium">{label(t) && <span className="num text-xs text-primary mr-1">{label(t)}</span>}{t.party_name || t.description}</div><div className="num text-xs text-muted-foreground">{t.branch && <b className="text-accent mr-1">{t.branch} ·</b>}{t.date} · {(t.pay_method || t.mode)}</div></div>
                      <span className="num font-semibold">{inr(t.amount)}</span>
                    </div>
                  ))}
                  {cashbook.receipts.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">None</div>}
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-4 py-3 bg-destructive/5 border-b border-border flex items-center gap-2 font-display text-lg font-semibold text-destructive"><TrendingDown size={16} /> Payments ({cashbook.expenses.length})</div>
                <div className="divide-y divide-border/70 max-h-[420px] overflow-auto">
                  {cashbook.expenses.map((t) => (
                    <div key={t.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <div className="min-w-0"><div className="truncate font-medium">{label(t) && <span className="num text-xs text-primary mr-1">{label(t)}</span>}{t.party_name || t.description}</div><div className="num text-xs text-muted-foreground">{t.branch && <b className="text-accent mr-1">{t.branch} ·</b>}{t.date} · {(t.pay_method || t.mode)}</div></div>
                      <span className="num font-semibold">{inr(t.amount)}</span>
                    </div>
                  ))}
                  {cashbook.expenses.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">None</div>}
                </div>
              </div>
            </div>
          </>
        )}

        {tab === "invoices" && invoicesRep && (
          <div className="animate-fade-up space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-md border border-border bg-card p-1 gap-1">
                {[{ v: "", l: "All" }, { v: "tata_motors", l: "Tata Motors" }, { v: "outside", l: "Outside" }].map((s) => (
                  <button key={s.v} data-testid={`inv-report-src-${s.v || "all"}`} onClick={() => { setInvSource(s.v); }}
                    className={`px-3 h-8 rounded text-xs font-medium transition-colors ${invSource === s.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{s.l}</button>
                ))}
              </div>
              <button data-testid="inv-report-apply" onClick={() => generate("invoices")} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors">Apply</button>
              <button data-testid="inv-report-export" onClick={exportInvoices} className="h-8 px-3 rounded-md border border-input bg-card text-xs flex items-center gap-1.5 hover:bg-muted transition-colors ml-auto"><FileDown size={14} /> CSV</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Invoices</div><div className="num text-xl font-bold">{invoicesRep.count}</div></div>
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Total value</div><div className="num text-xl font-bold">{inr(invoicesRep.total_amount)}</div></div>
              <div className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-3"><div className="text-xs text-muted-foreground">Collected</div><div className="num text-xl font-bold text-accent">{inr(invoicesRep.total_paid)}</div></div>
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3"><div className="text-xs text-muted-foreground">Outstanding</div><div className="num text-xl font-bold text-destructive">{inr(invoicesRep.total_balance)}</div></div>
              {invoicesRep.total_discount > 0 && <div className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-3"><div className="text-xs text-muted-foreground">Discounts given</div><div className="num text-xl font-bold text-accent">{inr(invoicesRep.total_discount)}</div></div>}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span className="rounded-md bg-muted px-3 py-1.5">Tata Motors: <b className="num text-foreground">{invoicesRep.by_source.tata_motors.count}</b> · {inr(invoicesRep.by_source.tata_motors.amount)}</span>
              <span className="rounded-md bg-muted px-3 py-1.5">Outside: <b className="num text-foreground">{invoicesRep.by_source.outside.count}</b> · {inr(invoicesRep.by_source.outside.amount)}</span>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="grid grid-cols-[auto_1.4fr_auto_auto_auto_auto] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>No.</span><span>Account / vehicle</span><span className="text-center">Source</span><span className="text-right">Amount</span><span className="text-right">Paid</span><span className="text-right">Balance</span>
              </div>
              <div className="divide-y divide-border max-h-[520px] overflow-auto">
                {invoicesRep.items.map((i) => (
                  <div key={i.id} data-testid={`inv-report-row-${i.id}`} className="grid grid-cols-[auto_1.4fr_auto_auto_auto_auto] gap-3 px-5 py-2.5 items-center text-sm">
                    <span className="num text-xs font-bold text-primary">{i.invoice_no}</span>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{i.account_name || i.party_name}</div>
                      <div className="num text-xs text-muted-foreground truncate">{i.date}{i.vehicle_number ? ` · ${i.vehicle_number}` : ""}</div>
                    </div>
                    <span className={`text-[10px] text-center px-2 py-0.5 rounded-full ${i.source === "tata_motors" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{i.source === "tata_motors" ? "Tata" : "Outside"}</span>
                    <span className="num text-right">{inr(i.amount)}</span>
                    <span className="num text-right text-accent">{inr(i.paid_amount)}{i.discount > 0 ? <span className="block text-[10px] text-muted-foreground">disc {inr(i.discount)}</span> : null}</span>
                    <span className={`num text-right font-semibold ${i.balance > 0 ? "text-destructive" : "text-muted-foreground"}`}>{inr(i.balance)}</span>
                  </div>
                ))}
                {invoicesRep.items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No invoices in this range.</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "discounts" && discounts && (
          <div className="animate-fade-up">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Discounted invoices</div><div className="num text-xl font-bold">{discounts.count}</div></div>
              <div className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-3"><div className="text-xs text-muted-foreground">Total discounts given</div><div className="num text-xl font-bold text-accent">{inr(discounts.total_discount)}</div></div>
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Parties</div><div className="num text-xl font-bold">{discounts.by_party.length}</div></div>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-hidden mb-5">
              <div className="px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">By party</div>
              <div className="divide-y divide-border">
                {discounts.by_party.map((p) => (
                  <div key={p.party_name} data-testid={`discount-party-${p.party_name.replace(/\s/g, "-").toLowerCase()}`} className="grid grid-cols-[1.5fr_auto_auto] gap-3 px-5 py-3 items-center text-sm">
                    <div className="min-w-0"><div className="font-medium truncate">{p.party_name}</div>{p.account_name ? <div className="text-xs text-muted-foreground truncate">A/c: {p.account_name}</div> : null}</div>
                    <span className="num text-muted-foreground text-xs">{p.count} inv</span>
                    <span className="num text-right font-bold text-accent">{inr(p.discount)}</span>
                  </div>
                ))}
                {discounts.by_party.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No discounts given in this range.</div>}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="grid grid-cols-[auto_1.4fr_auto_auto] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>No.</span><span>Account</span><span className="text-right">Amount</span><span className="text-right">Discount</span>
              </div>
              <div className="divide-y divide-border max-h-[420px] overflow-auto">
                {discounts.items.map((i) => (
                  <div key={i.id} className="grid grid-cols-[auto_1.4fr_auto_auto] gap-3 px-5 py-2.5 items-center text-sm">
                    <span className="num text-xs font-bold text-primary">{i.invoice_no}</span>
                    <div className="min-w-0"><div className="font-medium truncate">{i.account_name || i.party_name}</div><div className="num text-xs text-muted-foreground">{i.date}</div></div>
                    <span className="num text-right">{inr(i.amount)}</span>
                    <span className="num text-right font-semibold text-accent">{inr(i.discount)}</span>
                  </div>
                ))}
                {discounts.items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No discounts given in this range.</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "credit" && credit && (
          <div className="animate-fade-up">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Credit parties</div><div className="num text-xl font-bold">{credit.count}</div></div>
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3"><div className="text-xs text-muted-foreground">Total outstanding</div><div className="num text-xl font-bold text-destructive">{inr(credit.total_outstanding)}</div></div>
              <div className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-3"><div className="text-xs text-muted-foreground">Over credit limit</div><div className="num text-xl font-bold text-accent">{credit.over_limit_count}</div></div>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-x-auto">
              <div className="min-w-[560px]">
              <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_120px_140px] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Account</span><span>Phone</span><span className="text-right">Limit</span><span className="text-right">Outstanding</span>
              </div>
              <div className="divide-y divide-border">
                {credit.parties.map((p) => (
                  <div key={p.id} data-testid={`credit-report-row-${p.id}`} className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_120px_140px] gap-3 px-5 py-3 items-center text-sm">
                    <div className="font-medium truncate">{p.name}{p.over_limit && <span className="ml-2 text-xs text-destructive">over limit</span>}</div>
                    <span className="num text-muted-foreground">{p.phone || "—"}</span>
                    <span className="num text-right">{p.credit_limit ? inr(p.credit_limit) : "—"}</span>
                    <span className={`num text-right font-bold ${p.over_limit ? "text-destructive" : ""}`}>{inr(p.outstanding)}</span>
                  </div>
                ))}
                {credit.parties.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No parties with outstanding credit.</div>}
              </div>
              </div>
            </div>
          </div>
        )}

        {tab === "flow" && flow && (
          <div className="animate-fade-up space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-[hsl(var(--positive))]/20 bg-[hsl(var(--positive))]/5 px-4 py-3"><div className="text-xs text-muted-foreground">Total received</div><div className="num text-xl font-bold text-[hsl(var(--positive))]">{inr(flow.receipts_total)}</div></div>
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3"><div className="text-xs text-muted-foreground">Total paid</div><div className="num text-xl font-bold text-destructive">{inr(flow.payments_total)}</div></div>
              <div className="rounded-lg border border-primary/15 bg-primary/5 px-4 py-3"><div className="text-xs text-muted-foreground">Net flow</div><div className={`num text-xl font-bold ${flow.net >= 0 ? "text-primary" : "text-destructive"}`}>{inr(flow.net)}</div></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="font-display text-lg font-semibold text-[hsl(var(--positive))] mb-3">Money In by method</div>
                {Object.entries(flow.receipts_by_method).length ? Object.entries(flow.receipts_by_method).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1.5 text-sm border-b border-border/60 last:border-0"><span className="capitalize text-muted-foreground">{k}</span><span className="num font-semibold">{inr(v)}</span></div>
                )) : <div className="text-sm text-muted-foreground">None</div>}
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="font-display text-lg font-semibold text-destructive mb-3">Money Out by method</div>
                {Object.entries(flow.payments_by_method).length ? Object.entries(flow.payments_by_method).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1.5 text-sm border-b border-border/60 last:border-0"><span className="capitalize text-muted-foreground">{k}</span><span className="num font-semibold">{inr(v)}</span></div>
                )) : <div className="text-sm text-muted-foreground">None</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "expenses" && expenses && (
          <div className="animate-fade-up space-y-5">
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 inline-block"><div className="text-xs text-muted-foreground">Total expenses</div><div className="num text-xl font-bold text-destructive">{inr(expenses.total)}</div></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-4 py-3 bg-muted font-display text-lg font-semibold">By category</div>
                <div className="divide-y divide-border/70">
                  {expenses.by_category.map((c) => (
                    <div key={c.category} data-testid={`exp-cat-${c.category}`} className="flex justify-between px-4 py-2.5 text-sm">
                      <span className="font-medium">{c.category}</span><span className="num font-semibold">{inr(c.total)}</span>
                    </div>
                  ))}
                  {expenses.by_category.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No expenses in range.</div>}
                </div>
              </div>
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-4 py-3 bg-muted font-display text-lg font-semibold">All expenses ({expenses.items.length})</div>
                <div className="divide-y divide-border/70 max-h-[420px] overflow-auto">
                  {expenses.items.map((t) => (
                    <div key={t.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <div className="min-w-0"><div className="truncate font-medium">{t.party_name || t.description}</div><div className="num text-xs text-muted-foreground">{t.date} · {t.category} · {t.mode}</div></div>
                      <span className="num font-semibold">{inr(t.amount)}</span>
                    </div>
                  ))}
                  {expenses.items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">None</div>}
                </div>
              </div>
            </div>
          </div>
        )}
        {tab === "cancelled" && cancelled && (
          <div className="animate-fade-up space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Cancelled invoices</div><div className="num text-xl font-bold">{cancelled.count}</div></div>
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3"><div className="text-xs text-muted-foreground">Original value cancelled</div><div className="num text-xl font-bold text-destructive">{inr(cancelled.total_cancelled)}</div></div>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>No.</span><span>Party / reason</span><span>Type</span><span className="text-right">Amount</span>
              </div>
              <div className="divide-y divide-border max-h-[480px] overflow-auto">
                {cancelled.items.map((t) => (
                  <div key={t.id} data-testid={`cancelled-row-${t.id}`} className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-5 py-3 items-center text-sm">
                    <span className="num text-xs font-bold text-primary">{t.invoice_no}</span>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.party_name}</div>
                      <div className="num text-xs text-muted-foreground truncate">{t.date}{t.cancel_reason ? ` · ${t.cancel_reason}` : ""}</div>
                    </div>
                    <span className="text-xs text-muted-foreground">{t.invoice_type || "—"}</span>
                    <span className="num text-right line-through text-muted-foreground">{inr(t.original_amount)}</span>
                  </div>
                ))}
                {cancelled.items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No cancelled invoices in this range.</div>}
              </div>
            </div>
          </div>
        )}
        {tab === "custom" && isStaff && <CustomReport />}
        {tab === "branches" && branchSum && <BranchSummary data={branchSum} />}
        {tab === "closing" && closing && <ClosingDiffReport data={closing} start={start} end={end} />}
        {(tab === "missing" || tab === "missingch") && missing && (
          <div className="animate-fade-up space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">{missing.field === "challan" ? "Challans present" : "Invoices present"}</div><div className="num text-xl font-bold">{missing.total_present}</div></div>
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3"><div className="text-xs text-muted-foreground">{missing.field === "challan" ? "Missing challans" : "Missing serials"}</div><div className="num text-xl font-bold text-destructive">{missing.total_missing}</div></div>
              <div className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-3"><div className="text-xs text-muted-foreground">Range</div><div className="num text-sm font-semibold">{missing.mode === "fy" ? `FY ${missing.fy}` : `${missing.start} → ${missing.end}`}</div></div>
            </div>
            {missing.total_missing === 0 ? (
              <div className="rounded-xl border border-[hsl(var(--positive))]/25 bg-[hsl(var(--positive))]/5 p-8 text-center">
                <div className="font-display text-lg font-semibold text-[hsl(var(--positive))]">{missing.field === "challan" ? "No missing challans 🎉" : "No missing serials 🎉"}</div>
                <div className="text-sm text-muted-foreground mt-1">Every {missing.field === "challan" ? "challan" : "serial"} number is accounted for in this {missing.mode === "fy" ? "financial year" : "range"}.</div>
              </div>
            ) : (
              <div className="space-y-4">
                {missing.groups.filter((g) => g.missing_count > 0).map((g) => (
                  <div key={g.stem} data-testid={`missing-group-${g.stem}`} className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="px-5 py-3 bg-muted flex flex-wrap items-center justify-between gap-2">
                      <div className="font-display text-base font-semibold">Series <span className="num">{g.stem || "(no prefix)"}</span></div>
                      <div className="text-xs text-muted-foreground num">Range {g.first} → {g.last} · <b className="text-foreground">{g.present_count}</b> present · <b className="text-destructive">{g.missing_count}</b> missing{g.truncated ? " · showing first 5000" : ""}</div>
                    </div>
                    <div className="p-4 flex flex-wrap gap-2">
                      {g.missing.map((m) => (
                        <span key={m.invoice_no} data-testid={`missing-serial-${m.invoice_no}`} className="num text-sm rounded-md border border-destructive/30 bg-destructive/5 text-destructive px-2.5 py-1 font-medium">{m.invoice_no}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {missing.no_serial && missing.no_serial.length > 0 && (
              <div className="rounded-lg border border-border bg-card px-4 py-3 text-xs text-muted-foreground">
                {missing.no_serial.length} invoice(s) have no numeric serial and were skipped: <span className="num">{missing.no_serial.slice(0, 10).join(", ")}{missing.no_serial.length > 10 ? "…" : ""}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
